import React, { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";

import clarityService from "../../newServices/clarity/clarity";
import useChatDispatchActions from "../../customHooks/useChatDispatchActions";
import { TOGGLE_RELEASE_NOTES_MODALS } from "../../helpers/releaseNotesConstants";

import Accordion from "../accordion/Accordion";
import BeautyScrollbar from "../beautyScrollbar/BeautyScrollbar";
import Loading from "../loading/Loading";
import CloseButton from "../closeButton/CloseButton";
import ReleaseNotePdfPreview from "./ReleaseNotePdfPreview";
import ReleaseNotePdfModal from "./ReleaseNotePdfModal";

import "./release-notes.scss";

/**
 * Histórico de versões (release notes).
 * Réplica do features/toolbar/version/version-history.tsx do Omnyve.CPU.UI:
 * lista as versões num accordion, cada uma com o preview do PDF e a modal
 * com o PDF inteiro. Os dados vêm da mesma API (clarity).
 *
 * A abertura/fechamento continua amarrada ao redux (modals do releaseNotes),
 * que é como o Omnify dispara este painel.
 */
function ReleaseNotes() {
  const dispatchActions = useChatDispatchActions();
  const { modals } = useSelector((state) => state.releaseNotes);

  const isOpen = modals.welcome || modals.presentation;

  const [loading, setLoading] = useState(true);
  const [releaseVersions, setReleaseVersions] = useState([]);
  const [openedId, setOpenedId] = useState(null);
  const [previewReleaseNote, setPreviewReleaseNote] = useState(null);

  const handleClose = () => {
    dispatchActions(TOGGLE_RELEASE_NOTES_MODALS, {
      welcome: false,
      presentation: false,
    });
  };

  const fetchReleaseNotes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await clarityService.getReleaseNotes();
      if (response && response.data) {
        setReleaseVersions(response.data);
      } else {
        setReleaseVersions([]);
      }
    } catch (e) {
      setReleaseVersions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchReleaseNotes();
    }
  }, [isOpen, fetchReleaseNotes]);

  const toggleAccordion = (id) => {
    setOpenedId((prev) => (prev === id ? null : id));
  };

  const handleOpenPreview = (releaseNote) => {
    setPreviewReleaseNote(releaseNote);
  };

  const handleClosePreview = () => {
    setPreviewReleaseNote(null);
  };

  if (!isOpen) {
    return ReactDOM.createPortal(null, document.getElementById("modal-root"));
  }

  const content = (
    <>
      <div className="help-center-ui show">
        <div className="help-center-header">
          <h5>Histórico de versões</h5>
          <CloseButton onClick={handleClose} />
        </div>
        <div className="help-center-body">
          {loading ? (
            <Loading />
          ) : releaseVersions.length === 0 ? (
            <div className="release-notes-empty">
              Nenhuma versão disponível.
            </div>
          ) : (
            <BeautyScrollbar className="help-center-scroll">
              <Accordion>
                <Accordion.Group>
                  {releaseVersions.map((version) => {
                    const fileUrl = version.link
                      ? clarityService.getReleaseNoteFileUrl(version.link)
                      : "";

                    return (
                      <Accordion.Item
                        key={version.id}
                        id={version.id}
                        title={`v - ${version.version}`}
                        opened={openedId === version.id}
                        toggleAccordion={toggleAccordion}
                      >
                        <div className="release-notes-version-content">
                          <div className="release-notes-version-info">
                            <span className="release-notes-version-title">
                              {version.title}
                            </span>
                            <span className="release-notes-version-date">
                              Publicado em: {version.release_date}
                            </span>
                          </div>
                          <ReleaseNotePdfPreview
                            releaseNote={version}
                            fileUrl={fileUrl}
                            onOpenPreview={() => handleOpenPreview(version)}
                          />
                        </div>
                      </Accordion.Item>
                    );
                  })}
                </Accordion.Group>
              </Accordion>
            </BeautyScrollbar>
          )}
        </div>
      </div>

      {previewReleaseNote && (
        <ReleaseNotePdfModal
          open={!!previewReleaseNote}
          releaseNote={previewReleaseNote}
          fileUrl={clarityService.getReleaseNoteFileUrl(previewReleaseNote.link)}
          onClose={handleClosePreview}
        />
      )}
    </>
  );

  return ReactDOM.createPortal(content, document.getElementById("modal-root"));
}

export default ReleaseNotes;
